'use client';

import React, { useEffect, useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import Link from 'next/link';
import { Box, Typography, Button } from '@mui/material';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { reloadForNewBuild, shouldReloadForNewBuild } from '@/lib/app/staleBuild';
import { enter, fadeUp, press } from './inbox/motion';

const INK = 'var(--ink)';
const ON_INK = 'var(--on-ink)';
const MUTED = 'var(--muted)';
const RULE = 'var(--rule)';
const SELECTED = 'var(--wash)';
const DANGER = { fg: 'var(--danger)', bg: 'color-mix(in srgb, var(--danger) 14%, transparent)' };

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

const pill = { ...press, height: 40, px: 2.25, borderRadius: 999, fontWeight: 600, textTransform: 'none', boxShadow: 'none' } as const;

export default function Error({ error, reset }: ErrorProps) {
  const dev = process.env.NODE_ENV === 'development';
  // After a deploy, an open tab can ask for code the new build doesn't have; a reload fixes it.
  const [reloading] = useState(() => shouldReloadForNewBuild(error));

  useEffect(() => {
    if (reloading) {
      reloadForNewBuild();
      return;
    }
    Sentry.captureException(error);
  }, [error, reloading]);

  if (reloading) return null;

  return (
    <Box sx={{ minHeight: '70vh', display: 'grid', placeItems: 'center', px: 3, py: 8 }}>
      <Box sx={{ width: '100%', maxWidth: 400, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', ...enter(fadeUp, 320) }}>
        <Box sx={{ position: 'relative', width: 64, height: 64, mb: 2.5 }}>
          <Box aria-hidden sx={{ position: 'absolute', inset: -10, borderRadius: '50%', bgcolor: DANGER.bg, opacity: 0.5 }} />
          <Box
            sx={{
              position: 'relative',
              width: 64,
              height: 64,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              bgcolor: DANGER.bg,
              color: DANGER.fg,
            }}
          >
            <ErrorOutlineRoundedIcon sx={{ fontSize: 30 }} />
          </Box>
        </Box>

        <Typography component="h1" sx={{ fontSize: '1.25rem', fontWeight: 650, color: INK, letterSpacing: '-0.015em' }}>
          Something went wrong
        </Typography>
        <Typography sx={{ mt: 0.75, maxWidth: 320, fontSize: '0.9375rem', lineHeight: 1.55, color: MUTED }}>
          This page hit an unexpected error. Try again, and if it keeps happening let us know.
        </Typography>

        {(dev || error.digest) && (
          <Box
            sx={{
              mt: 3,
              width: '100%',
              px: 1.75,
              py: 1.5,
              borderRadius: 2,
              bgcolor: SELECTED,
              border: `1px solid ${RULE}`,
              textAlign: 'left',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: '0.8125rem',
              lineHeight: 1.5,
              color: INK,
              wordBreak: 'break-word',
            }}
          >
            {dev && <Box>{error.message}</Box>}
            {error.digest && <Box sx={{ mt: dev ? 0.75 : 0, color: MUTED }}>Reference {error.digest}</Box>}
          </Box>
        )}

        <Box sx={{ mt: 3, display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            disableElevation
            startIcon={<RefreshRoundedIcon />}
            onClick={reset}
            sx={{ ...pill, bgcolor: INK, color: ON_INK, '&:hover': { bgcolor: INK, opacity: 0.88, boxShadow: 'none' } }}
          >
            Try again
          </Button>
          <Button
            component={Link}
            href="/"
            variant="outlined"
            sx={{ ...pill, borderColor: RULE, color: INK, '&:hover': { borderColor: INK, bgcolor: 'transparent' } }}
          >
            Go home
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
